/**
 * Platform-wide date formatting.
 *
 * The house style is `dd-MMM-yyyy` (e.g. `25-Aug-2026`) — unambiguous across UK/EU/US
 * audiences, unlike locale-dependent numeric formats where 03/08 flips meaning. Use these
 * helpers instead of `toLocaleDateString`/`toLocaleString` for any user-facing calendar date
 * so the whole product reads consistently.
 *
 * Values are rendered in the viewer's LOCAL time zone (matching the previous
 * `toLocaleString` behaviour). Invalid / missing inputs render as an em dash.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const EMPTY = '—';

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** `25-Aug-2026`. */
export function formatDate(value: DateInput): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return `${pad2(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

/** `25-Aug-2026 14:30` (24-hour local time). */
export function formatDateTime(value: DateInput): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return `${formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * `25-Aug-2026 14:30:07` (24-hour local time, second precision).
 *
 * Use where the exact moment matters and updates arrive live — device heartbeats
 * pushed over the `/bff/hubs/devices` SignalR hub carry full ISO-8601 precision,
 * and minute truncation hides the change.
 */
export function formatDateTimeWithSeconds(value: DateInput): string {
  const d = toDate(value);
  if (!d) return EMPTY;
  return `${formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}
